import { Result } from '@/types/result';

// Используется any так как мы используем интерфейсы для типов моделей вместо type

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Params = Record<string, any>;

const SESSION_EXPIRED_MESSAGE = 'Сессия истекла. Обновите страницу и войдите заново.';
// Браузер уверен, что сети нет
const OFFLINE_ERROR_MESSAGE = 'Нет соединения с интернетом. Проверьте подключение.';
// Сеть есть, но запрос не дошёл до сервера (сервер лежит/перезапускается, DNS, CORS)
const SERVER_UNREACHABLE_MESSAGE = 'Сервер недоступен. Попробуйте повторить позже.';

/**
 * Человекочитаемый текст по HTTP-статусу — чтобы пользователь видел «Нет прав»,
 * а не «403 Forbidden». Для 2xx-ошибок бизнес-логики текст приходит от сервера
 * в самом Result и сюда не попадает.
 *
 * Код статуса добавляем в скобках: разные причины (401/403, разные 5xx) сводятся
 * к одному тексту, а код помогает тех-поддержке понять, что именно произошло.
 */
function statusErrorMessage(status: number): string {
    switch (status) {
        case 401:
        case 403:
            return `Нет прав (${status})`;
        case 404:
            return 'Данные не найдены';
        default:
            return status >= 500
                ? `Сбой на сервере, попробуйте позже (${status})`
                : `Не удалось выполнить запрос (${status})`;
    }
}

/**
 * Текст для сбоя, не дошедшего до ответа сервера. Отделяем «нет интернета»
 * от «сервер недоступен»: раньше всё сваливалось в один текст про интернет,
 * и при лежащем сервере пользователь напрасно проверял своё подключение.
 */
function networkErrorMessage(): string {
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return isOffline ? OFFLINE_ERROR_MESSAGE : SERVER_UNREACHABLE_MESSAGE;
}

/**
 * Ошибка запроса с уже готовым человекочитаемым текстом (по статусу или истёкшей
 * сессии). Отличается от сырого сбоя fetch, у которого текст технический
 * («Failed to fetch») — такой мы заменяем на понятное сообщение о связи.
 *
 * Экспортируется для потребителей с throwError: сервер ответил статусом, значит
 * запрос точно не выполнился — им можно показать message как есть, а не общий
 * текст про «ответ не получен, проверьте, сохранилось ли».
 */
export class ApiError extends Error {}

/**
 * Ответ при сбое запроса.
 *
 * Раньше при сетевой ошибке / не-2xx ответе методы возвращали undefined, и вызывающий
 * код падал на `result.isSuccess` с TypeError вместо того, чтобы отработать штатную
 * ветку отказа. Возвращаем проваленный Result, чтобы отказ выглядел так же, как
 * неуспех от сервера, и обрабатывался тем же кодом.
 *
 * Ошибки с 2xx (Result.isSuccess === false) сюда не попадают — они разбираются
 * потребителями как обычно. key пустой: текст в value уже готов к показу.
 */
export function failedResult(error: unknown): Result {
    return {
        entity: null,
        isSuccess: false,
        errors: [
            {
                key: '',
                value: error instanceof ApiError ? error.message : networkErrorMessage(),
            },
        ],
        httpStatusCode: 0,
        successMessage: '',
    };
}

/**
 * Признак того, что вместо ожидаемых данных вернулась ошибка запроса.
 * Нужен для эндпоинтов, которые отдают модель напрямую, минуя Result
 * (в контроллерах они помечены комментарием «не использовать Result»):
 * у их успешного ответа поля isSuccess нет.
 */
export function isFailResult(value: unknown): value is Result {
    return Boolean(value)
        && typeof value === 'object'
        && (value as Result).isSuccess === false
        && Array.isArray((value as Result).errors);
}

/**
 * Один раз за жизнь страницы уводим браузер на другой URL. Флаг защищает от
 * дублей, когда сразу несколько параллельных запросов вернули ошибку: редирект
 * происходит один раз, лишние обработчики не рисуют тосты и не падают.
 *
 * Возвращаем «подвисающий» промис: страница уже уходит, поэтому вызывающий код
 * не должен продолжать выполнение (иначе в момент навигации мелькнёт тост или
 * упадёт обращение к полю отсутствующего результата).
 */
let isRedirecting = false;
function redirectAway(url: string): Promise<never> {
    if (!isRedirecting) {
        isRedirecting = true;
        window.location.assign(url);
    }
    return new Promise<never>(() => { /* никогда не резолвится — ждём навигации */ });
}

/**
 * Уже ли мы на странице, на которую собираемся уводить. Защищает от бесконечной
 * перезагрузки: например на самой странице логина глобальный компонент шлёт GET,
 * тот снова упирается в неавторизованность и редиректит на логин — и так по кругу,
 * потому что isRedirecting сбрасывается на каждой новой загрузке страницы.
 * Сравниваем только путь: query (ReturnUrl и т.п.) у логина отличается.
 */
function isCurrentPage(url: string): boolean {
    try {
        return new URL(url, window.location.origin).pathname === window.location.pathname;
    } catch {
        return false;
    }
}

/**
 * Собирает URL страницы логина, куда уводить при истёкшей сессии.
 *
 * fetch проходит cookie-редирект и отдаёт response.url вида
 * /Account/Login?ReturnUrl=<путь XHR-эндпоинта> (например /Lesson/HasNextLessoninSeria).
 * Возвращаться туда после логина нельзя — сервер отдаст голый JSON эндпоинта вместо
 * страницы. Поэтому берём путь логина из ответа, но ReturnUrl проставляем на текущую
 * страницу, где пользователь и находился.
 */
function toLoginUrl(redirectedUrl: string): string {
    const loginUrl = new URL(redirectedUrl, window.location.origin);
    loginUrl.search = '';
    loginUrl.searchParams.set('ReturnUrl', window.location.pathname + window.location.search);
    return loginUrl.toString();
}

export class ApiService {
    protected base = '';
    protected getUrl(method: string): string {
        if (!this.base) {
            return `/${method}`;
        }
        return `/${this.base}/${method}`;
    }
    /**
     * @param redirectOnError уводить ли на /Error/{status} при не-2xx (кроме 404).
     *   По умолчанию включено: GET тянет данные страницы, и на не-2xx она нерабочая.
     *   Отключается для запросов, которые не должны рушить текущий экран при
     *   транзиентной ошибке — например фоновые/поллинговые запросы звонка, где
     *   потребитель сам показывает ошибку и не уводит пользователя из звонка.
     */
    protected async get(url: string, params?: Params, redirectOnError = true) {
        try {
            const queryUrl = new URL(url, window.location.origin);
            const search = this.paramsToSearch(params);
            if (search) {
                queryUrl.search = search.toString();
            }
            const response = await fetch(queryUrl.toString(), {
                headers: {
                    'Content-Type': 'application/json;charset=utf-8'
                },
            });
            // не-2xx (кроме 404) уводит на страницу ошибки, как раньше document.write
            return await handleResponse(response, redirectOnError);
        } catch (err) {
            console.error(err);
            return failedResult(err);
        }
    }

    protected async put(url: string, body: Params | number | string, useQuery = false) {
        try {
            const queryUrl = new URL(url, window.location.origin);
            if (useQuery) {
                const search = this.paramsToSearch(body as Params);
                if (search) {
                    queryUrl.search = search.toString();
                }
                const response = await fetch(queryUrl.toString(), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8'
                    },
                });
                return await handleResponse(response, false);
            }
            const response = await fetch(queryUrl.toString(), {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json;charset=utf-8'
                },
            });
            return await handleResponse(response, false);
        } catch (err) {
            console.error(err);
            return failedResult(err);
        }
    }

    protected async delete(url: string, body: Params | number | string, useQuery = false) {
        try {
            const queryUrl = new URL(url, window.location.origin);
            if (useQuery) {
                const search = this.paramsToSearch(body as Params);
                if (search) {
                    queryUrl.search = search.toString();
                }
                const response = await fetch(queryUrl.toString(), {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8'
                    },
                });
                return await handleResponse(response, false);
            }
            const response = await fetch(queryUrl.toString(), {
                method: 'DELETE',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json;charset=utf-8'
                },
            });
            return await handleResponse(response, false);

        } catch (err) {
            console.error(err);
            return failedResult(err);
        }
    }

    protected async post(url: string, body?: Params | number | string, useQuery = false, throwError = false) {
        try {
            const queryUrl = new URL(url, window.location.origin);
            if (!body) {
                const response = await fetch(queryUrl.toString(), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8'
                    },
                });

                return await handleResponse(response, false);
            }
            if (useQuery) {
                const search = this.paramsToSearch(body as Params);
                if (search) {
                    queryUrl.search = search.toString();
                }
                const response = await fetch(queryUrl.toString(), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8'
                    },
                });
                return await handleResponse(response, false);
            }
            const response = await fetch(queryUrl.toString(), {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json;charset=utf-8'
                },
            });
            return await handleResponse(response, false);
        } catch (err) {
            console.error(err);
            // Вызывающий сам обрабатывает ошибку (например, ретраит запрос)
            if (throwError) {
                throw err;
            }
            return failedResult(err);
        }
    }

    private paramsToSearch(params?: Params): URLSearchParams | null {
        if (!params) {
            return null;
        }

        return new URLSearchParams(params);
    }
}

/**
 * @param allowErrorPageRedirect уводить ли на /Error/{status} при не-2xx. Включено
 *   только для GET (данные страницы): на не-2xx там страница нерабочая. Мутации
 *   (POST/PUT/DELETE) возвращают проваленный Result, чтобы потребитель показал тост
 *   и не потерял введённые данные формы.
 *
 * Штатные ошибки бизнес-логики приходят со статусом 200 и Result.isSuccess === false —
 * сюда они не попадают и обрабатываются потребителями как обычно.
 */
async function handleResponse(response: Response, allowErrorPageRedirect: boolean) {
    if (response.ok) {
        // Истёкшая сессия: cookie-схема редиректит на /Account/Login (302), fetch
        // проходит редирект, и вместо JSON приходит 200 + HTML страницы логина.
        if (response.redirected) {
            // На обычных страницах уводим на логин. Но если запрос попросил не
            // уводить со страницы (redirectOnError: false — например поллинг звонка)
            // либо мы уже на странице логина (иначе бесконечная перезагрузка),
            // не рвём экран: возвращаем понятный отказ.
            if (allowErrorPageRedirect && !isCurrentPage(response.url)) {
                return redirectAway(toLoginUrl(response.url));
            }
            throw new ApiError(SESSION_EXPIRED_MESSAGE);
        }
        return await response.json();
    }

    // 404 на XHR исторически не считается страницей (эндпоинт/данные не найдены) —
    // раньше он просто пробрасывался, минуя document.write. Оставляем это поведение:
    // возвращаем проваленный Result, без ухода на /Error/404.
    const errorPageUrl = `/Error/${response.status}`;
    if (allowErrorPageRedirect && response.status !== 404 && !isCurrentPage(errorPageUrl)) {
        return redirectAway(errorPageUrl);
    }

    throw new ApiError(statusErrorMessage(response.status));
}
